import { useCallback, useEffect, useRef } from "react";

export const SINGLE_CLICK_DELAY = 150;

// One pending action across components: a later click replaces the earlier one.
let pending: { owner: symbol; timer: ReturnType<typeof setTimeout> } | undefined;
let subscribers = 0;
const cancelEvents = ["pointerdown", "pointercancel", "dblclick", "wheel", "gesturestart", "touchstart", "blur", "keydown", "contextmenu"];

function cancelPendingClick() {
  if (!pending) return;
  clearTimeout(pending.timer);
  pending = undefined;
}

/**
 * Delay the entire single-click action, including selection and editor activation.
 * Call schedule from onClick (not pointerdown); dragging and double-click actions
 * remain immediate. A context change or unmount cancels this owner's action.
 * This hook has no dependency on a particular object, plan or editor type.
 */
export function useDelayedClick(contextKey: string, delay = SINGLE_CLICK_DELAY) {
  const owner = useRef(Symbol("delayed-click"));
  const cancel = useCallback(() => {
    if (pending?.owner === owner.current) cancelPendingClick();
  }, []);
  const schedule = useCallback((action: () => void, clickCount = 1) => {
    cancelPendingClick();
    if (clickCount > 1) return;
    pending = {
      owner: owner.current,
      timer: setTimeout(() => {
        pending = undefined;
        action();
      }, delay),
    };
  }, [delay]);
  useEffect(() => {
    if (subscribers++ === 0) {
      for (const name of cancelEvents) window.addEventListener(name, cancelPendingClick, true);
    }
    return () => {
      cancel();
      if (--subscribers === 0) {
        for (const name of cancelEvents) window.removeEventListener(name, cancelPendingClick, true);
      }
    };
  }, [cancel, contextKey]);
  return { schedule, cancel };
}
